import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { MessageSquare, Phone, PhoneIncoming, PhoneOutgoing, Mail, RefreshCw, Loader2, User, Clock, CheckCircle, Send, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { formatPhone } from '../../utils/formatPhone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function InboxPage() {
  const [activeTab, setActiveTab] = useState('sms');
  const [smsMessages, setSmsMessages] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [replyDialog, setReplyDialog] = useState({ open: false, message: null });
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [smsRes, callsRes] = await Promise.all([
        axios.get(`${API}/telnyx/sms/inbound`),
        axios.get(`${API}/telnyx/calls`)
      ]);
      setSmsMessages(smsRes.data.messages || []);
      setUnreadCount(smsRes.data.unread_count || 0);
      setCalls(callsRes.data.calls || []);
    } catch (error) {
      console.error('Failed to fetch inbox:', error);
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Poll for new messages every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleMarkRead = async (smsId) => {
    try {
      await axios.put(`${API}/telnyx/sms/${smsId}/read`);
      setSmsMessages(prev => prev.map(m => m.id === smsId ? { ...m, read: true } : m));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !replyDialog.message) return;
    setSending(true);
    try {
      await axios.post(`${API}/telnyx/sms`, {
        to_number: replyDialog.message.from_number,
        message: replyText,
        lead_id: replyDialog.message.lead_id
      });
      toast.success('Reply sent');
      setReplyDialog({ open: false, message: null });
      setReplyText('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="inbox-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inbox</h1>
          <p className="text-slate-500">Inbound SMS messages and call history</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="sms" className="flex items-center gap-2" data-testid="tab-sms">
            <MessageSquare className="h-4 w-4" />
            SMS
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-5 flex items-center justify-center text-xs">
                {unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="calls" className="flex items-center gap-2" data-testid="tab-calls">
            <Phone className="h-4 w-4" />
            Calls
          </TabsTrigger>
        </TabsList>

        {/* SMS Tab */}
        <TabsContent value="sms" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                Inbound SMS Messages
              </CardTitle>
              <CardDescription>
                Messages received from leads and contacts
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : smsMessages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No inbound SMS messages yet</p>
                  <p className="text-sm text-slate-400 mt-1">Messages will appear here when leads reply to your SMS</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {smsMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-4 rounded-lg border ${!msg.read ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'}`}
                      data-testid={`sms-${msg.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-full ${!msg.read ? 'bg-blue-100' : 'bg-slate-100'}`}>
                            <MessageSquare className={`h-4 w-4 ${!msg.read ? 'text-blue-600' : 'text-slate-500'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">
                                {msg.lead_name || formatPhone(msg.from_number)}
                              </span>
                              {msg.lead_id && (
                                <Link to={`/admin/leads/${msg.lead_id}`}>
                                  <Badge variant="outline" className="text-xs cursor-pointer hover:bg-slate-100">
                                    <User className="h-3 w-3 mr-1" />
                                    View Lead
                                  </Badge>
                                </Link>
                              )}
                              {!msg.read && (
                                <Badge className="bg-blue-600 text-xs">New</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{msg.message}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {msg.created_at ? format(parseISO(msg.created_at), 'MMM d, h:mm a') : ''}
                              </span>
                              <span>From: {formatPhone(msg.from_number)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!msg.read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkRead(msg.id)}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Mark Read
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setReplyDialog({ open: true, message: msg });
                              if (!msg.read) handleMarkRead(msg.id);
                            }}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Reply
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calls Tab */}
        <TabsContent value="calls" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-green-600" />
                Call History
              </CardTitle>
              <CardDescription>
                Inbound and outbound call records
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : calls.length === 0 ? (
                <div className="text-center py-12">
                  <Phone className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No call history yet</p>
                  <p className="text-sm text-slate-400 mt-1">Calls made from the CRM will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {calls.map((call) => (
                    <div
                      key={call.id}
                      className="p-4 rounded-lg border bg-white border-slate-200 flex items-center justify-between"
                      data-testid={`call-${call.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${call.direction === 'inbound' ? 'bg-green-100' : 'bg-blue-100'}`}>
                          {call.direction === 'inbound' ? (
                            <PhoneIncoming className="h-4 w-4 text-green-600" />
                          ) : (
                            <PhoneOutgoing className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">
                              {call.lead_name || formatPhone(call.direction === 'inbound' ? call.from_number : call.to_number)}
                            </span>
                            {call.lead_id && (
                              <Link to={`/admin/leads/${call.lead_id}`}>
                                <Badge variant="outline" className="text-xs cursor-pointer hover:bg-slate-100">
                                  <User className="h-3 w-3 mr-1" />
                                  View Lead
                                </Badge>
                              </Link>
                            )}
                            <Badge variant={call.direction === 'inbound' ? 'default' : 'secondary'} className="text-xs">
                              {call.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {call.created_at ? format(parseISO(call.created_at), 'MMM d, h:mm a') : ''}
                            </span>
                            <span>
                              {call.direction === 'inbound' ? 'From' : 'To'}: {formatPhone(call.direction === 'inbound' ? call.from_number : call.to_number)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          call.status === 'completed' ? 'text-green-600 border-green-200 bg-green-50' :
                          call.status === 'answered' ? 'text-blue-600 border-blue-200 bg-blue-50' :
                          call.status === 'ringing' ? 'text-yellow-600 border-yellow-200 bg-yellow-50' :
                          'text-slate-600'
                        }
                      >
                        {call.status || 'unknown'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reply Dialog */}
      <Dialog open={replyDialog.open} onOpenChange={(open) => !open && setReplyDialog({ open: false, message: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reply to {replyDialog.message?.lead_name || formatPhone(replyDialog.message?.from_number)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">{replyDialog.message?.message}</p>
              <p className="text-xs text-slate-400 mt-1">
                {replyDialog.message?.created_at ? format(parseISO(replyDialog.message.created_at), 'MMM d, h:mm a') : ''}
              </p>
            </div>
            <Textarea
              placeholder="Type your reply..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={3}
              data-testid="reply-text"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyDialog({ open: false, message: null })}>
              Cancel
            </Button>
            <Button onClick={handleReply} disabled={sending || !replyText.trim()}>
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send Reply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
